import React, { useLayoutEffect, useRef, useState } from 'react';
import HumanTyping from '../ui/HumanTyping';
import TypingIndicator from '../TypingIndicator';
import TivFragment from './TivFragment';
import TivButtons from './TivButtons';

/**
 * @param {{
 *   messages: import('../../types/tiv').TivMessage[],
 *   isWaiting: boolean,
 *   onTypingDone: () => void,
 *   onSend: (text: string) => void
 * }} props
 */
export default function TivFeed({ messages, isWaiting, onTypingDone, onSend }) {
  const containerRef    = useRef(null);
  const spacerRef       = useRef(null);
  const bottomRef       = useRef(null);
  const itemRefs        = useRef({});
  const lastUserIdRef       = useRef(null); // dernier msg user scrollé
  const initialScrollDone   = useRef(false);

  // IDs présents au montage → affichés sans animation
  const [doneMsgs] = useState(() => new Set(messages.map(m => m.id)));
  // IDs dont le typing est terminé → déclenche l'affichage du fragment
  const [typedMsgs, setTypedMsgs] = useState(() => new Set(messages.map(m => m.id)));

  const visible = messages.filter(m => m.sender !== 'casper');

  const TOP_GAP = 12;

  useLayoutEffect(() => {
    const container = containerRef.current;

    // Chargement initial : scroll instantané pour mettre le dernier msg user en haut
    if (!initialScrollDone.current) {
      initialScrollDone.current = true;
      const lastUserMsg = [...visible].reverse().find(m => m.sender === 'user');
      if (lastUserMsg && container && spacerRef.current) {
        const lastEl  = itemRefs.current[lastUserMsg.id];
        if (lastEl) {
          spacerRef.current.style.minHeight =
            `${Math.max(0, container.clientHeight - lastEl.offsetHeight - TOP_GAP)}px`;
          const elTop = lastEl.getBoundingClientRect().top - container.getBoundingClientRect().top;
          container.scrollBy({ top: elTop - TOP_GAP });
        }
      }
      return;
    }

    const lastUserMsg = [...visible].reverse().find(m => m.sender === 'user');

    // Pas de message user, ou message user issu du chargement initial → scroll en bas
    if (!lastUserMsg || doneMsgs.has(lastUserMsg.id)) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    // Même message user que le scroll précédent → réponse bot arrivée, ne pas bouger
    if (lastUserMsg.id === lastUserIdRef.current) return;

    // Nouveau message user → scroll vers lui avec spacer
    lastUserIdRef.current = lastUserMsg.id;

    const userEl = itemRefs.current[lastUserMsg.id];
    if (!userEl || !container || !spacerRef.current) return;

    spacerRef.current.style.minHeight =
      `${Math.max(0, container.clientHeight - userEl.offsetHeight - TOP_GAP)}px`;

    const elTop = userEl.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollBy({ top: elTop - TOP_GAP, behavior: 'smooth' });
  }, [messages]); // eslint-disable-line

  function handleTypingDone(msgId) {
    doneMsgs.add(msgId);
    setTypedMsgs(prev => new Set([...prev, msgId]));
    onTypingDone();
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto no-scrollbar px-4 py-6 flex flex-col gap-4"
    >
      {visible.map(m => {
        if (m.sender === 'user') {
          const text = typeof m.message?.content === 'string' ? m.message.content : '';
          return (
            <div
              key={m.id}
              ref={el => { itemRefs.current[m.id] = el; }}
              className="self-end bg-blue-700/50 px-4 py-3 rounded-2xl rounded-br-sm max-w-[80%] leading-relaxed whitespace-pre-line break-words"
            >
              {text}
            </div>
          );
        }

        if (m.sender === 'tiv') {
        const content      = m.message?.content;
        const text         = typeof content === 'string' ? content : (content?.message ?? '');
        const fragment     = typeof content === 'object' && content?.fragment_text ? content : null;
        const buttons      = typeof content === 'object' && content?.buttons?.length ? content : null;
        const step_signal  = typeof content === 'object' && content?.step_signal ? content.step_signal : null;
          function handleButtonSubmit(text) {
            onSend(text);
          }

          return (
            <div key={m.id} ref={el => { itemRefs.current[m.id] = el; }} className="self-start flex gap-3 w-[85%]">
              <div className="w-7 h-7 rounded-lg bg-blue-600/20 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-extrabold text-blue-400">T</span>
              </div>
              <div className="flex flex-col gap-3 w-full">
                <div className="bg-white/[0.04] border border-white/[0.06] rounded-2xl rounded-bl-sm px-4 py-3">
                  <HumanTyping
                    firstLoad={doneMsgs.has(m.id)}
                    text={text}
                    sentenceDone={handleTypingDone}
                    messageId={m.id}
                  />
                </div>
                {buttons && typedMsgs.has(m.id) && visible[visible.length - 1]?.id === m.id && (
                  <TivButtons
                    buttons={buttons.buttons}
                    buttonsType={buttons.buttons_type}
                    onSubmit={handleButtonSubmit}
                  />
                )}
                {fragment && step_signal === 'fragment_produced' && typedMsgs.has(m.id) && (
                  <TivFragment
                    fragmentId={fragment.fragment_id}
                    fragmentText={fragment.fragment_text}
                    split3b={fragment.split_3b}
                  />
                )}
              </div>
            </div>
          );
        }

        return null;
      })}

      {isWaiting && (
        <div className="self-start flex gap-3">
          <div className="w-7 h-7 rounded-lg bg-blue-600/20 flex items-center justify-center shrink-0">
            <span className="text-xs font-extrabold text-blue-400">T</span>
          </div>
          <div className="bg-white/[0.04] border border-white/[0.06] rounded-2xl px-4 py-3">
            <TypingIndicator />
          </div>
        </div>
      )}

      <div ref={spacerRef} aria-hidden />
      <div ref={bottomRef} aria-hidden />
    </div>
  );
}
